import type MarkdownIt from 'markdown-it';

type MarkdownToken = ReturnType<MarkdownIt['parse']>[number];

const escapeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const parseVideoAttributes = (source: string) => {
  const attributes: Record<string, string> = {};
  for (const line of source.split('\n')) {
    const separator = line.indexOf(':');
    if (separator < 1) continue;
    const key = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (key && value) attributes[key] = value;
  }
  return attributes;
};

export const courseVideoPlugin = (md: MarkdownIt) => {
  md.block.ruler.before('fence', 'course_video', (state, startLine, endLine, silent) => {
    const start = state.bMarks[startLine] + state.tShift[startLine];
    const end = state.eMarks[startLine];
    if (state.src.slice(start, end).trim() !== ':::video') return false;

    let nextLine = startLine + 1;
    while (nextLine < endLine) {
      const lineStart = state.bMarks[nextLine] + state.tShift[nextLine];
      const lineEnd = state.eMarks[nextLine];
      if (state.src.slice(lineStart, lineEnd).trim() === ':::') break;
      nextLine += 1;
    }
    if (nextLine >= endLine) return false;
    if (silent) return true;

    const contentStart = state.eMarks[startLine] + 1;
    const contentEnd = state.bMarks[nextLine];
    const attributes = parseVideoAttributes(state.src.slice(contentStart, contentEnd));
    if (attributes.provider !== 'youtube' || !/^[\w-]{11}$/.test(attributes.id ?? '')) {
      return false;
    }

    const title = attributes.title ?? 'Vídeo do curso';
    const caption = attributes.caption;
    const embedUrl = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(attributes.id)}`;
    const token = state.push('html_block', '', 0);
    token.map = [startLine, nextLine + 1];
    token.content = [
      `<div class="course-video" data-course-video-provider="youtube" data-course-video-id="${escapeAttribute(attributes.id)}">`,
      `<iframe src="${embedUrl}" title="${escapeAttribute(title)}" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>`,
      caption ? `<p class="course-video-caption">${escapeAttribute(caption)}</p>` : '',
      '</div>\n',
    ].join('');
    state.line = nextLine + 1;
    return true;
  });
};

const calloutTypes = {
  atencao: 'attention',
  conceito: 'concept',
  dica: 'tip',
  exemplo: 'example',
  importante: 'important',
} as const;

const normalizeCalloutLabel = (label: string) =>
  label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const calloutTypeForTokens = (tokens: MarkdownToken[], index: number) => {
  for (let tokenIndex = index + 1; tokenIndex < Math.min(tokens.length, index + 5); tokenIndex += 1) {
    const token = tokens[tokenIndex];
    if (token?.type !== 'inline') continue;
    const label = token.content.match(/^\s*\*\*([^*]+)\*\*/)?.[1];
    if (!label) return null;
    return calloutTypes[normalizeCalloutLabel(label) as keyof typeof calloutTypes] ?? null;
  }
  return null;
};

export const courseCalloutPlugin = (md: MarkdownIt) => {
  md.renderer.rules.blockquote_open = (tokens, index, options, _env, self) => {
    const type = calloutTypeForTokens(tokens, index);
    return type
      ? `<blockquote class="course-callout course-callout-${type}">`
      : self.renderToken(tokens, index, options);
  };
};
